import { useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import ActionButton from '@core/components/actionbutton/ActionButton';
import LinkButton from '@core/components/linkbutton/LinkButton';
import Modal from '@core/components/Modal/Modal';
import UseModal from 'hooks/use-modal';
import UseToast from 'hooks/use-toast';

import MedalStarIcon from 'assets/images/pages/public-certificate/certificate-badge.svg';
import ArrowDownIcon from 'assets/icons/arrow-down.svg';
import CourseCertificateImage from 'assets/images/pages/public-certificate/public-certificate.png';
import LinkedInIcon from 'assets/icons/linkedin-primary.svg';
import GermanyFlag from '@core/assets/icons/germany-flag-24.svg';

import { CopySuccess, MedalStar } from 'iconsax-react';
import { MOBILE_FULLSCREEN } from '@core/constants/ModalTypes';

import SurveyCertificateWrapper from 'components/pages/lecture/lecturemainbutton/surveycertificatewrapper/SurveyCertificateWrapper';

import classes from './PublicCertificate.module.scss';
import ContentCurriculum from 'components/common/contentcurriculum/ContentCurriculum';

const StarIcon = () => (
	<svg width="16" height="16" viewBox="0 0 24 24" fill="#faa61a" xmlns="http://www.w3.org/2000/svg">
		<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
	</svg>
);

const getYearMonth = (dateStr) => {
	if (!dateStr) return { year: '', month: '' };
	const [, month, year] = dateStr.split('/');
	return { year, month };
};

const getExpirationYear = (year) => {
	const numYear = parseInt(year, 10);
	return isNaN(numYear) ? '' : numYear + 2;
};

const PublicCertificate = ({ data }) => {
	const [expanded, setExpanded] = useState(false);
	const { show: certificateVisible, showModal: showCertificate, destroyModal: closeCertificate } = UseModal();
	const { toast, ToastContainer } = UseToast();
	const router = useRouter();

	const handleAccordionChange = (panel) => (_, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const { name, is_owner, lecture, feedback, learners_count } = data;

	const { year, month } = getYearMonth(data.certificate_generated_at);
	const expirationYear = getExpirationYear(year);

	const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=${encodeURIComponent(lecture.title)}&name=${encodeURIComponent(lecture.title)}&organizationId=76549552&issueYear=${year}&issueMonth=${month}&expirationYear=${expirationYear}&expirationMonth=${month}&certUrl=${encodeURIComponent(`https://pharmuni.com/public-certificates/${router.query.uuid}/${router.query.slug}`)}&certId=${router.query.uuid}`;

	return (
		<>
		<ToastContainer />
		<Modal type={MOBILE_FULLSCREEN} closeHandler={closeCertificate} show={certificateVisible} backgroundColor='#FFF5E5'>
			<SurveyCertificateWrapper lectureId={lecture.id} />
		</Modal>
		<div className="container-fluid p-0">
			{/* Hero Section */}
			<section className={classes['hero']}>
				<div className="container">
					<div className="row align-items-center">
						<div className="col-12 col-md-7">
							<div className={classes['badge']}>
								<MedalStarIcon />
								<span>Course Certificate</span>
							</div>

							<p className={classes['hero-subtitle']}>
								<strong>{name}</strong> completed this course and earned a Pharmuni certificate.
							</p>

							<h1 className={classes['hero-title']}>{lecture.title}</h1>

							<div className={classes['cta-wrapper']}>
								{is_owner ? (
									<>
										<ActionButton
											kind="primary"
											onClick={showCertificate}
										>
											Download Course Certificate
										</ActionButton>
										<ActionButton
											kind="secondary"
											onClick={() => {
												navigator.clipboard.writeText(window.location.href);
												toast('Link copied to clipboard!');
											}}
										>
											<div className='d-flex align-items-center gap-2'>
												<CopySuccess size={16} />
												Copy URL
											</div>
										</ActionButton>
										<ActionButton
											kind="secondary"
											onClick={() => window.open(linkedInUrl, '_blank', 'noopener,noreferrer')}
										>
											<div className='d-flex align-items-center gap-2'>
												<LinkedInIcon />
												Share on LinkedIn
											</div>
										</ActionButton>
									</>
								) : (
									<LinkButton
										kind="primary-color"
										to={`/courses/${lecture.id}/${lecture.slug}`}
										className={classes['cta-button']}
									>
										Start Learning
									</LinkButton>
								)}
							</div>

							{feedback && (
								<div className={classes['review-card']}>
									<div className={classes['review-header']}>
										<span className={classes['review-name']}>{name}</span>
										<StarIcon />
										<span className={classes['review-rate']}>{feedback.rate}</span>
									</div>
									<p className={classes['review-comment']}>
										&ldquo;{feedback.comment}&rdquo;
									</p>
								</div>
							)}
						</div>

						<div className="col-12 col-md-5 d-flex justify-content-center justify-content-md-end">
							<div className={classes['certificate-image-wrapper']}>
								<Image
									src={CourseCertificateImage}
									alt="Pharmuni Course Certificate"
									className={classes['certificate-image']}
								/>
								<div className={classes['certificate-info']}>
									<h2>ISO 9001</h2>
									<h3>Quality Management System</h3>
									<p>Certificate Registration No.: 12 100 60610 TMS</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Stats Bar */}
			<section className={classes['stats-bar']}>
				<div className="container">
					<div className={classes['stats-row']}>
						<div className={classes['stat-item']}>
							<span className={classes['stat-value']}>{learners_count}</span>
							<span className={classes['stat-label']}>Number of users:</span>
						</div>
						<div className={classes['stat-divider']} />
						<div className={classes['stat-item']}>
							<span className={classes['stat-value']}>
								<StarIcon />
								&nbsp;{lecture.rate_average}
							</span>
							<span className={classes['stat-label']}>User Reviews</span>
						</div>
						<div className={classes['stat-divider']} />
						<div className={classes['stat-item']}>
							<span className={classes['stat-value']}>96%</span>
							<span className={classes['stat-label']}>Satisfaction Rate</span>
						</div>
						<div className={classes['stat-divider']} />
						<div className={classes['stat-item']}>
							<span className={`${classes['stat-value']} ${classes['stat-icon']}`}>
								<MedalStar size={24} color='#FAA61A' variant='Bold' />
							</span>
							<span className={classes['stat-label-bold']}>ISO 9001</span>
						</div>
						<div className={classes['stat-divider']} />
						<div className={classes['stat-item']}>
							<span className={classes['stat-value']}><GermanyFlag /></span>
							<span className={classes['stat-label-bold']}>Made in Germany</span>
						</div>
					</div>
				</div>
			</section>


			{/* About Section */}
			<section className={classes['about-section']}>
				<div className="container px-5">
					<h2 className={classes['section-heading']}>About the {lecture.title}</h2>
					<div
						className={classes['section-description']}
						dangerouslySetInnerHTML={{ __html: lecture.description }}
					/>
				</div>
			</section>

			{/* Learning Objectives */}
			<section className={classes['objectives-section']}>
				<div className="container px-5">
					<span className={classes['objectives-label']}>Learning Objectives</span>
					{lecture.learning_objectives_description && (
						<p className={classes['objectives-description']}>
							{lecture.learning_objectives_description}
						</p>
					)}
					<ContentCurriculum data={lecture.learning_objectives} />					
				</div>
			</section>
		</div>
		</>
	);
};

export default PublicCertificate;
